import React, { Component } from 'react';
import { withStyles, WithStyles } from '@material-ui/core';
import { compose } from 'recompose';
import moment from 'moment';
import { times } from 'lodash';

import { hoursInDay, minutesInHour, tenMinutes } from './Constants';
import { gridTemplateRows } from './Calendar';
import { CalendarColumn } from './CalendarColumn';
import { withDefaultSettings } from './styles';
import { GridHeader } from './GridHeader';
import { CalendarGridProps, ColorScheme, Dimensions, CalendarEvent } from '../../../types';

const styles = (theme: any) => ({
  root: {
    borderLeft: ({ colorScheme }: { colorScheme: ColorScheme }) => `1px solid ${colorScheme.divider}`,
    borderTop: ({ colorScheme }: { colorScheme: ColorScheme }) => `1px solid ${colorScheme.divider}`,
    backgroundColor: ({ colorScheme }: { colorScheme: ColorScheme }) => colorScheme.header,
    overflowX: 'hidden',
    flex: '1 0 0%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  events: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  sideline: {
    borderRight: ({ colorScheme }: { colorScheme: ColorScheme }) => `1px solid ${colorScheme.divider}`,
    borderBottom: ({ colorScheme }: { colorScheme: ColorScheme }) => `1px solid ${colorScheme.divider}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--primary-bg)',
  },
  grid: {
    flex: '1 0 0%',
    display: 'grid',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: ({ columns, dimensions }: { columns: any[]; dimensions: Dimensions }) =>
        `${dimensions.timeLineWidth}px repeat(${columns.length}, 1fr)`,
      gridTemplateRows: ({
        startHour,
        endHour,
        dimensions,
      }: {
        startHour: number;
        endHour: number;
        dimensions: Dimensions;
      }) =>
        gridTemplateRows(
          hoursInDay,
          startHour,
          endHour,
          `${dimensions.tenMinutesHeightB * (minutesInHour / tenMinutes)}px`
        ),
    },
    [theme.breakpoints.only('sm')]: {
      gridTemplateColumns: ({ columns, dimensions }: { columns: any[]; dimensions: Dimensions }) =>
        `${dimensions.timeLineWidth}px repeat(${columns.length}, 1fr)`,
      gridTemplateRows: ({
        startHour,
        endHour,
        dimensions,
      }: {
        startHour: number;
        endHour: number;
        dimensions: Dimensions;
      }) =>
        gridTemplateRows(
          hoursInDay,
          startHour,
          endHour,
          `${dimensions.tenMinutesHeightM * (minutesInHour / tenMinutes)}px`
        ),
    },
    [theme.breakpoints.only('xs')]: {
      gridTemplateColumns: ({ columns, dimensions }: { columns: any[]; dimensions: Dimensions }) =>
        `${dimensions.timeLineWidth}px repeat(${columns.length}, 1fr)`,
      gridTemplateRows: ({
        startHour,
        endHour,
        dimensions,
      }: {
        startHour: number;
        endHour: number;
        dimensions: Dimensions;
      }) =>
        gridTemplateRows(
          hoursInDay,
          startHour,
          endHour,
          `${dimensions.tenMinutesHeightS * (minutesInHour / tenMinutes)}px`
        ),
    },
    gridRowGap: 0,
    gridColumnGap: 0,
  },
});

// Create a type that combines our component props with the injected styles
type Props = CalendarGridProps & WithStyles<typeof styles>;

class CalendarGridComponent extends Component<Props> {
  static defaultProps = {
    getColumnEvents: (date: moment.Moment, { events }: { events: CalendarEvent[] }) =>
      events.filter(
        ({ start, end }) => moment(start).isSameOrBefore(date, 'day') && moment(end).isSameOrAfter(date, 'day')
      ),
    getColumnDate: (date: any) => date,
    columns: [moment.utc()],
    renderTime: (date: moment.Moment) => date.format('LT'),
    renderColumnName: (date: moment.Moment) => date.format('D MMM'),
  };

  render() {
    const {
      classes,
      style,
      columns,
      endHour,
      startHour,
      renderTime,
      getColumnEvents,
      getColumnDate,
      onTimeSlotClick,
      ...rest
    } = this.props;

    return (
      <div className={classes.root} style={style}>
        <GridHeader columns={columns} {...rest} />
        <div className={classes.events}>
          <div className={classes.grid}>
            {times(endHour - startHour, (t) => {
              const hour = t + startHour;
              return (
                <div
                  key={hour}
                  className={classes.sideline}
                  style={{
                    gridColumn: '1 / 2',
                    gridRow: `${hour + 1} / ${hour + 2}`,
                  }}
                >
                  {renderTime(moment({ hour }))}
                </div>
              );
            })}
            {columns.map((column, index) => (
              <CalendarColumn
                {...rest}
                key={index + '-calendar-column'}
                style={{
                  gridRow: `1 /${hoursInDay + 1}`,
                  gridColumn: `${index + 2} /${index + 3}`,
                }}
                onTimeSlotClick={onTimeSlotClick}
                startHour={startHour}
                endHour={endHour}
                date={getColumnDate(column)}
                events={getColumnEvents(column, this.props)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
}

export const CalendarGrid = compose<Props, CalendarGridProps>(
  withDefaultSettings,
  withStyles(styles)
)(CalendarGridComponent);
